import {
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import WSDataGrid, { IColumns } from "../../../Components/DataGrid/WSDataGrid";
import { useNavigate } from "react-router-dom";
import { updateMainHeader, callAPI, Method } from "../../../commonFunctions/CommonFunctions";
import { breadCrumbsStore } from "../../../Components/BreadCrumbs/BreadCrumbsListner";
import { useDispatch } from "react-redux";
import { useSnackbar } from "../../../Components/Snackbar/SnackBarContext";
import { UpdateStatus } from "../../Reports/subComponents/UpdateStatus";

export const ChequeEntryList: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showMessage } = useSnackbar();

  // ✅ State
  const [isStatusPopupOpen, setStatusPopupOpen] = useState(false);
  const [selectedChequeId, setSelectedChequeId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // ✅ Form
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      newStatus: "",
    },
  });

  useEffect(() => {
    updateMainHeader("Cheque Management");
  }, []);

  // ✅ Breadcrumbs
  breadCrumbsStore.updateBreadCrumbs([
    { "Cheque Status": "/chequestatus/list" },
  ]);

  // ✅ Status Options
  const statusOptions = [
    { value: "Pending", label: "Pending" },
    { value: "Deposited", label: "Deposited" },
    { value: "Cleared", label: "Cleared" },
    { value: "Bounced", label: "Bounced" },
  ];

  // ✅ API Source
  const source = {
    url: "cheques/list",
    page: 1,
    pageSize: 10,
    filter: [],
    sort: [],
    type: "GET" as const,
  };

  // ✅ Handle Update Status API
  const handleUpdateStatus = async (data: any) => {
    try {
      if (!selectedChequeId) return;

      const body = {
        Status: data.newStatus,
      };

      const response = await callAPI(
        `cheques/${selectedChequeId}/status`,
        Method.PUT,
        body
      );

      if (response?.success) {
        showMessage("Cheque status updated successfully!", "success");
        setStatusPopupOpen(false);
        reset();

        // 🔄 Refresh grid
        setRefreshKey((prev) => prev + 1);
      } else {
        showMessage(response?.message || "Failed to update status", "error");
      }
    } catch (error: any) {
      console.error("❌ Update Status Error:", error);
      showMessage(error?.message || "Something went wrong", "error");
    }
  };

  // ✅ Columns
  const columns: IColumns[] = [
    { header: "Cheque Number", fieldName: "ChequeNumber" },
    { header: "Cheque Date", fieldName: "ChequeDate", type: "date" as const },
    { header: "Bank Name", fieldName: "BankName" },
    { header: "Party Type", fieldName: "PartyType" },
    { header: "Party Name", fieldName: "PartyName" },
    { header: "Amount", fieldName: "Amount", type: "amount" as const },
    { header: "Due Date", fieldName: "DueDate", type: "date" as const },
    { header: "Status", fieldName: "Status", type: "status" as const },
    {
      header: "Actions",
      fieldName: "actions",
      type: "action" as const,
      actions: ["Update Status"],
      actionCallback: (action: string, row: any) => {
        const ChequeId = row.ChequeId || row.id;

        if (!ChequeId) {
          console.error("❌ No valid ID found in row:", row);
          showMessage("Invalid cheque data - missing ID", "error");
          return;
        }

        if (action === "Update Status") {
          setSelectedChequeId(ChequeId.toString());
          setStatusPopupOpen(true);
        }
      },
    },
  ];

  // ✅ Add Button
  const handleClick = () => {
    navigate("/chequeentry");
  };

  return (
    <>
      <Grid p={3}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <WSDataGrid
              key={refreshKey}
              id="chequeEntryGrid"
              title="Cheque List"
              headerType="modern"
              columns={columns}
              source={source}
              exportCsv
              rowsPerPageCount={10}
              searchBox={{ searchMode: "query" }}
              addButton={{ label: "Add New Cheque", color: "secondary" }}
              onAddButtonClick={handleClick}
              gridHeaderSx={{
                backgroundColor: "whitesmoke",
                border: "1px solid whitesmoke",
              }}
            />
          </Grid>
        </Grid>
      </Grid>

      {/* ✅ Update Status Dialog */}
      <Dialog open={isStatusPopupOpen} onClose={() => setStatusPopupOpen(false)}>
        <DialogTitle>Update Cheque Status</DialogTitle>

        <DialogContent>
          <UpdateStatus
            control={control}
            errors={{}}
            register={register}
            id={selectedChequeId}
            reset={reset}
            config={statusOptions}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setStatusPopupOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit(handleUpdateStatus)}
            disabled={!isValid}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};